import { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  MdPlayArrow as PlayArrowIcon,
  MdPause as PauseIcon,
} from "react-icons/md";
import { Spinner } from "react-bootstrap";

import { useNowPlaying } from "@/components/contexts/now-playing";
import { usePlayerState } from "@/components/contexts/player";
import useRequest from "src/lib/use-request";
import { startOfDay } from "src/lib/events";
import { formatStartTime, nextShow, scheduleFeedUrl } from "src/lib/schedule";

import styles from "./HeroBackground.module.css";

// Nothing here counts down; this only exists to move "Up next" on to the
// following show once the current one has started. A minute is close enough.
const TICK = 60000;

const showQuery = gql`
  query HeroShow($id: ID!) {
    showById(id: $id) {
      id
      showName
    }
  }
`;

/**
 * Hero backdrop: a single image, built server-side (the on-air show's banner
 * when it has one, otherwise a mosaic of recent album covers — see the
 * backend's `generateherobanner` command) rather than laid out here as 72
 * separate tiles.
 *
 * The lower third is one slot with three mutually exclusive states, never
 * stacked and never boxed — a broadcast lower third says one thing at a
 * time: a live DJ show is on air; automation is playing with a show coming
 * up later; or, lacking either, a plain invitation to listen live. Only the
 * first and third carry a play button — pressing play during "Up next"
 * wouldn't start that show, so it doesn't offer to.
 */
function MosaicBackdrop({ banner }) {
  const { playlist } = useNowPlaying();
  const { isPlaying, isLoading, toggleLivestream } = usePlayerState();
  const showId = playlist?.show?.id;
  const isLive = Boolean(showId);

  const { data: liveData } = useQuery(showQuery, {
    variables: { id: showId },
    skip: !isLive,
  });
  const liveShow = liveData?.showById;

  // `now` is deliberately client-only. The server has no useful clock for a
  // countdown, and deriving one during render would disagree with the
  // browser on hydration.
  const [now, setNow] = useState(null);
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), TICK);
    return () => clearInterval(id);
  }, []);

  // Keyed on the day rather than the moment, so the tick above re-renders
  // without refetching the feed every minute.
  const dayStart = now ? startOfDay(now).getTime() : null;
  const feedUrl =
    !isLive && dayStart ? scheduleFeedUrl(new Date(dayStart)) : null;
  const { data: entries } = useRequest(feedUrl, { revalidateOnFocus: false });
  const upNextShow = useMemo(
    () => (entries && now ? nextShow(entries, now) : null),
    [entries, now]
  );

  let credit;
  if (isLive) {
    credit = liveShow && {
      label: "On air",
      name: liveShow.showName,
      href: `/show/${liveShow.id}/`,
      playable: true,
    };
  } else if (upNextShow) {
    credit = {
      label: "Up next",
      name: `${upNextShow.title} · ${formatStartTime(upNextShow.startsAt)}`,
      href: "/schedule/",
      playable: false,
    };
  } else {
    credit = {
      label: "Listen live",
      name: playlist?.show?.show_name || "KVRX Automation",
      href: "/schedule/",
      playable: true,
    };
  }

  let playerIcon;
  if (isLoading) {
    playerIcon = (
      <Spinner animation="border" role="status" size="sm">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  } else if (isPlaying) {
    playerIcon = <PauseIcon aria-hidden="true" />;
  } else {
    playerIcon = <PlayArrowIcon aria-hidden="true" />;
  }

  return (
    <>
      <div className={styles.artLayer} aria-hidden="true">
        {banner ? (
          <div
            className={styles.banner}
            style={{ backgroundImage: `url(${banner.url})` }}
          />
        ) : null}
      </div>
      <div className={styles.scrim} aria-hidden="true" />
      <div className={styles.dim} aria-hidden="true" />
      <div className={styles.halo} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />
      {credit ? (
        <div className={styles.credit}>
          {credit.playable ? (
            <button
              type="button"
              className={styles.creditPlay}
              aria-label={isPlaying ? "Pause live stream" : "Play live stream"}
              aria-pressed={isPlaying}
              disabled={isLoading}
              onClick={toggleLivestream}
            >
              {playerIcon}
            </button>
          ) : null}
          <a className={styles.creditLink} href={credit.href}>
            <span className={styles.creditRule} aria-hidden="true" />
            <span className={styles.creditText}>
              <span className={styles.creditLabel}>{credit.label}</span>
              <span className={styles.creditName}>{credit.name}</span>
            </span>
          </a>
        </div>
      ) : null}
    </>
  );
}

export default MosaicBackdrop;
